import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import promoCodeApi, { CouponType, SubscriptionType, UserReferralInfo } from '@/services/promotionApi';

interface PromoCodeState {
  couponList: CouponType[];
  applyingSubscriptions: SubscriptionType[];
  subscriptions: SubscriptionType[];
  redeemStatus: string | number;
  userReferralInfo: UserReferralInfo;
}

const initialState: PromoCodeState = {
  couponList: [],
  applyingSubscriptions: [],
  subscriptions: [],
  redeemStatus: '',
  userReferralInfo: {
    myLink: '',
    visitCount: 0,
    totalReferrals: 0,
    totalReward: 0,
    availableReward: 0,
    rewardCurrency: 'HKD',
    rewards: [
      {
        code: '',
        currency: 'HKD',
        amount: 0,
        referralAccount: '',
        createDate: '',
        availableDate: '',
        status: '',
        redeemBy: '',
      },
    ],
  },
};

export const getCouponList = createAsyncThunk(
  'promotion/couponList',
  async (currency: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await promoCodeApi.getCouponList(currency);
      const { errorCode } = response;
      if (errorCode === '0') {
        return response.coupons;
      }
      throw new Error('');
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getSubscriptionsList = createAsyncThunk(
  'promotion/subscriptionList',
  async (
    params: { lang: string; couponCode?: string; callBack?: Function },
    { rejectWithValue, dispatch, getState },
  ) => {
    const { lang, couponCode, callBack } = params;
    try {
      const response = await promoCodeApi.getSubscriptionsList(lang, couponCode);
      const { errorCode, applyingSubscriptions, subscriptions } = response;
      if (errorCode === '0') {
        if (callBack) {
          callBack(subscriptions);
        }
        return { applyingSubscriptions, subscriptions };
      }
      throw new Error('');
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const redeemCouponAtPlatform = createAsyncThunk(
  'promotion/redeemCouponAtPlatform',
  async (
    params: { orderId: number; couponCode: string; callBack: Function },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const { orderId, couponCode, callBack } = params;
      const response = await promoCodeApi.redeemCouponAtPlatform(orderId, couponCode);
      const { errorCode } = response;
      if (errorCode) {
        if (callBack) {
          callBack(errorCode);
        }
        return errorCode;
      }
      throw new Error('');
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getUserReferralInfo = createAsyncThunk(
  'promotion/referralProgram',
  async (params, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await promoCodeApi.getUserReferralInfo();
      const { errorCode, myLink, visitCount, totalReferrals, totalReward, availableReward, rewardCurrency, rewards } =
        response;
      if (errorCode === '0') {
        return {
          myLink,
          visitCount,
          totalReferrals,
          totalReward,
          availableReward,
          rewardCurrency,
          rewards,
        };
      }
      throw new Error('');
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);
export const deleteSubscription = createAsyncThunk(
  'promotion/deleteSubscription',
  async (params: { orderId: number }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { orderId } = params;
      const response = await promoCodeApi.deleteSubscription(orderId);
      const { errorCode } = response;
      if (errorCode === '0') {
        return errorCode;
      }
      throw new Error('');
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const promoCodeSlice = createSlice({
  name: 'promotion',
  initialState,
  // reducers is when setting states on Redux State, except for states from apis
  reducers: {},
  // extraReducers is when setting states from apis
  extraReducers: (builder) => {
    // setting couponList value
    builder.addCase(getCouponList.fulfilled, (state, action) => {
      state.couponList = action.payload;
    });
    // setting applyingSubscriptions and subscriptions values
    builder.addCase(getSubscriptionsList.fulfilled, (state, action) => {
      state.applyingSubscriptions = action.payload.applyingSubscriptions;
      state.subscriptions = action.payload.subscriptions;
    });
    // setting redeemStatus
    builder.addCase(redeemCouponAtPlatform.fulfilled, (state, action) => {
      state.redeemStatus = action.payload;
    });
    // setting userReferalInfo
    builder.addCase(getUserReferralInfo.fulfilled, (state, action) => {
      state.userReferralInfo = action.payload;
    });
  },
});

export default promoCodeSlice.reducer;
